import { formatCount } from '@/lib/format'

/**
 * Compact horizontal split of managed resources into "real" infrastructure (aws/gcp/azure/...)
 * vs "internal-only" logical resources (random/tls/null/...). Reused on the Dashboard and the
 * State workspace header.
 *
 * `real`/`internal` are null when a version hasn't been classified yet (legacy rows, pre-072
 * pushes, or a classifier bailout). When BOTH are null/undefined the bar is not rendered - the
 * caller shows its own "not yet classified" affordance. A genuine all-internal workspace is a
 * valid `real: 0` and still renders.
 */
export function ResourceSplitBar({
  real,
  internal,
  compact = false,
}: {
  real: number | null | undefined
  internal: number | null | undefined
  /** compact = thinner bar + inline counts, for table rows / tight headers. */
  compact?: boolean
}) {
  if ((real ?? null) === null && (internal ?? null) === null) return null

  const r = real ?? 0
  const i = internal ?? 0
  const total = r + i
  const realPct = total > 0 ? (r / total) * 100 : 0

  const height = compact ? 6 : 10
  const REAL_COLOR = 'var(--accent)'
  const INTERNAL_COLOR = 'var(--text3)'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 3 : 6, minWidth: compact ? 90 : 160 }}>
      <div
        role="img"
        aria-label={`${formatCount(r)} real, ${formatCount(i)} internal managed resources`}
        style={{ display: 'flex', height, borderRadius: height / 2, overflow: 'hidden', background: 'var(--bg3)' }}
      >
        {r > 0 && <div style={{ width: `${realPct}%`, background: REAL_COLOR }} />}
        {i > 0 && <div style={{ flex: 1, background: INTERNAL_COLOR, opacity: 0.55 }} />}
      </div>
      <div style={{ display: 'flex', gap: 12, fontFamily: 'var(--mono)', fontSize: compact ? '0.6rem' : '0.66rem' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text2)' }}>
          <span style={{ width: 7, height: 7, borderRadius: 2, background: REAL_COLOR, flexShrink: 0 }} />
          {formatCount(r)} real
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text3)' }}>
          <span style={{ width: 7, height: 7, borderRadius: 2, background: INTERNAL_COLOR, opacity: 0.55, flexShrink: 0 }} />
          {formatCount(i)} internal
        </span>
      </div>
    </div>
  )
}
